import { t } from './i18n.ts'
import type { MicAccess } from './deviceProbe'

/**
 * Which "how to allow the microphone" walkthrough to show, and how sure we are.
 *
 * THE RISK THIS MANAGES. Every browser hides the permission control somewhere
 * different — Chrome desktop puts it behind the address-bar icon, Chrome on
 * Android behind ⋮ → Site settings, Firefox behind the padlock's permissions
 * panel, and iOS Safari has no useful mid-page path at all. A walkthrough that
 * confidently shows the WRONG chrome is worse than showing none: a nine-year-old
 * hunting for an icon that isn't there concludes the app is broken and stops
 * trying. So platform detection here reports CONFIDENCE, and anything less than
 * confident falls back to advice that is true everywhere.
 *
 * userAgentData is preferred where it exists (it survives the UA-reduction that
 * makes UA strings increasingly useless); the UA string is the fallback. Neither
 * is trustworthy inside an in-app WebView or on an iPad in desktop mode, which is
 * exactly why `confident` exists.
 */

export type MicPlatform =
  | 'chrome-android'
  | 'chromium-desktop'
  | 'edge-desktop'
  | 'firefox-desktop'
  | 'ios'
  | 'generic'

export interface PlatformGuess {
  platform: MicPlatform
  confident: boolean
  /** Human label for the disclosure header, e.g. "Chrome on Android". */
  label: string
}

interface UaDataBrand {
  brand: string
  version: string
}
interface UaData {
  brands?: UaDataBrand[]
  mobile?: boolean
  platform?: string
}

export function detectPlatform(): PlatformGuess {
  const nav = navigator as Navigator & { userAgentData?: UaData }
  const ua = navigator.userAgent || ''
  const data = nav.userAgentData

  // iOS first, and iPadOS with it: every browser there is WebKit under the hood,
  // so the Chrome-on-iOS case must NOT get Chrome-on-Android instructions.
  const iOSLike =
    /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1)
  if (iOSLike) return { platform: 'ios', confident: true, label: t('mic.platform.ios') }

  // An in-app WebView (Facebook, Instagram, Messenger) renders our chrome-less
  // page inside another app. There is no address bar and often no way to reach
  // site settings at all, so never pretend to give steps.
  if (/\bFBAN|\bFBAV|Instagram|Line\/|; wv\)/.test(ua)) {
    return { platform: 'generic', confident: false, label: t('mic.platform.generic') }
  }

  const brands = data?.brands?.map((b) => b.brand) ?? []
  const isMobile = data?.mobile ?? /Android|Mobile/.test(ua)
  const android = data?.platform === 'Android' || /Android/.test(ua)
  const firefox = /Firefox|FxiOS/.test(ua)
  const chromium =
    brands.some((b) => /Chromium|Google Chrome|Microsoft Edge/.test(b)) || /Chrome|CriOS|Edg/.test(ua)
  // Checked BEFORE the generic Chromium branch, and by the Edge-specific marker,
  // because Edge's UA also contains "Chrome". A child told to look for Chrome's
  // chrome while sitting in Edge is exactly the confidently-wrong guidance the
  // confidence gate exists to prevent — and on Windows, Edge is the default
  // browser, so this is not a rare path.
  const edge = brands.some((b) => /Microsoft Edge/i.test(b)) || /Edg[A-Z]?\//.test(ua)

  if (android && chromium) {
    return { platform: 'chrome-android', confident: true, label: t('mic.platform.chromeAndroid') }
  }
  if (edge && !isMobile) {
    return { platform: 'edge-desktop', confident: true, label: t('mic.platform.edgeDesktop') }
  }
  if (firefox && !isMobile) {
    return { platform: 'firefox-desktop', confident: true, label: t('mic.platform.firefoxDesktop') }
  }
  if (chromium && !isMobile) {
    return { platform: 'chromium-desktop', confident: true, label: t('mic.platform.chromiumDesktop') }
  }
  // Android Firefox, Samsung Internet, desktop Linux oddities, anything new:
  // true-everywhere advice rather than a confident guess.
  return { platform: 'generic', confident: false, label: t('mic.platform.generic') }
}

export type HelpPhoto = 'chrome-desktop-site'

export interface HelpStep {
  /** Localised instruction line. */
  text: string
  art: 'address-bar' | 'menu-dots' | 'site-settings' | 'mic-allow' | 'padlock' | 'ios-settings' | 'device-pick' | 'picker-tab' | 'picker-audio' | 'mac-privacy' | 'focus-window'
  /** Real browser screenshot. Permission walks use this; no photo = text only. */
  photo?: HelpPhoto
}

export type HelpDevice = 'mic' | 'camera'

/**
 * The walkthrough. `missing` and `busy` are NOT permission problems, so they get
 * their own advice — telling a child to allow a microphone they do not have is
 * the confident-wrongness this module exists to prevent.
 */
export function helpSteps(platform: MicPlatform, access: MicAccess, device: HelpDevice = 'mic'): HelpStep[] {
  const prefix = device === 'camera' ? 'cam' : 'mic'
  const copy = (key: string) => t(`${prefix}.steps.${key}` as Parameters<typeof t>[0])
  if (access === 'missing') {
    return [
      { text: copy('missing.1'), art: 'device-pick' },
      { text: copy('missing.2'), art: 'mic-allow' },
    ]
  }
  if (access === 'busy') {
    return [
      { text: copy('busy.1'), art: 'device-pick' },
      { text: copy('busy.2'), art: 'mic-allow' },
    ]
  }
  // Silence is NOT a permission problem, so the permission walkthrough below
  // would send someone to a setting that is already correct. The causes are
  // physical or OS-level, and on the Windows desktops most of our classes run on
  // they are, in rough order of how often they turn out to be the answer: an
  // inline mute switch on the headset cable, the wrong input endpoint selected,
  // and Windows' own per-app microphone privacy toggle. Ordered that way on
  // purpose — the first step is the one a child can check without leaving the
  // page, and platform makes no difference to any of them.
  if (access === 'silent' && device === 'mic') {
    return [
      { text: t('mic.steps.silent.1'), art: 'mic-allow' },
      { text: t('mic.steps.silent.2'), art: 'device-pick' },
      { text: t('mic.steps.silent.3'), art: 'site-settings' },
    ]
  }

  switch (platform) {
    case 'chrome-android':
      return [
        { text: copy('chromeAndroid.1'), art: 'menu-dots' },
        { text: copy('chromeAndroid.2'), art: 'site-settings' },
        { text: copy('chromeAndroid.3'), art: 'mic-allow' },
      ]
    case 'chromium-desktop':
      return [
        { text: copy('chromiumDesktop.1'), art: 'address-bar', photo: 'chrome-desktop-site' },
        { text: copy('chromiumDesktop.2'), art: 'mic-allow', photo: 'chrome-desktop-site' },
      ]
    case 'edge-desktop':
      // Edge's flow is the same shape as Chrome's but the panel is named
      // differently ("Permissions for this site"), and naming it correctly is the
      // difference between a child finding it and giving up.
      return [
        { text: copy('edgeDesktop.1'), art: 'address-bar' },
        { text: copy('edgeDesktop.2'), art: 'mic-allow' },
      ]
    case 'firefox-desktop':
      return [
        { text: copy('firefoxDesktop.1'), art: 'padlock' },
        { text: copy('firefoxDesktop.2'), art: 'mic-allow' },
      ]
    case 'ios':
      // Safari genuinely has no reliable mid-page unblock, so this is the one
      // platform where the honest instruction is "leave and come back".
      return [
        { text: copy('ios.1'), art: 'ios-settings' },
        { text: copy('ios.2'), art: 'mic-allow' },
      ]
    default:
      return [
        { text: copy('generic.1'), art: 'site-settings' },
        { text: copy('generic.2'), art: 'mic-allow' },
      ]
  }
}
